// pages/admin/ventas/VentasNueva.jsx
import React, { useState, useEffect, useRef } from 'react';
import { App, Button, Alert, Result, Descriptions, Tag, Space, Typography, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined, ClockCircleOutlined, CloseOutlined,
  CheckOutlined, CheckCircleFilled, PlusOutlined, HomeOutlined,
  ExclamationCircleFilled, FieldTimeOutlined
} from '@ant-design/icons';

import SeleccionTallerHorario from './SeleccionTallerHorario';
import BusquedaUsuario from './BusquedaUsuario';
import SeleccionPlan from './SellecionPlan';
import ResumenYPago from './ResumenYPago';
import VentasPresenciales from '../../../../service/VentaPresencial';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Text } = Typography;

const formatCLP = (n) =>
  n != null ? `$${Number(n).toLocaleString('es-CL')}` : '–';

const formatReloj = (seg) => {
  const s = Math.max(0, seg || 0);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const STEPS = [
  { title: 'Taller & Horario',  subtitle: 'Recinto, taller y horarios' },
  { title: 'Cliente',            subtitle: 'Identificar al comprador'   },
  { title: 'Plan de Pago',       subtitle: 'Modalidad y descuentos'     },
  { title: 'Confirmar y Cobrar', subtitle: 'Resumen y método de pago'   },
];

// Estados del cobro Webpay (los informa ResumenYPago) en los que la venta ya no
// se puede cancelar ni abandonar desde aquí: hay un QR vivo o el pago ya entró.
const COBRO_EN_CURSO = ['pendiente', 'revision', 'completada'];

const ESTADO_INICIAL_VENTA = {
  items: [], ventaId: null, codigoVenta: null, tallerId: null,
  horarioIds: [], taller: null, horarios: [], planNombre: null,
  planDetalle: null, codigoDescuentoManual: null, descuentoManual: null,
  usuario: null, inscrito: null, esVecino: false, cargas: [],
  tipoInscripcion: 'para_mi', expiraEn: null,
  advertencias: []
};

const idsDeVenta = (v) => [...new Set([
  ...(v.items || []).map(i => i.ventaId),
  v.ventaId
].filter(Boolean))];

const VentasNueva = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { modal } = App.useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading]         = useState(false);
  const [completed, setCompleted]     = useState(false);
  const [ventaData, setVentaData]     = useState(ESTADO_INICIAL_VENTA);
  const [segundos, setSegundos]       = useState(null);
  const [expirada, setExpirada]       = useState(false);
  const [estadoPago, setEstadoPago]   = useState('libre');
  const ventaCompletadaRef = useRef(null);
  const avisosRef = useRef(new Set());

  const hayVenta     = idsDeVenta(ventaData).length > 0 && !completed;
  const cobroEnCurso = COBRO_EN_CURSO.includes(estadoPago);

  // ── Timer ──────────────────────────────────────────────────────────────────
  // En segundos: antes contaba minutos redondeando hacia abajo cada 30 s, así que
  // mostraba "0 min" con hasta un minuto de reserva, y al llegar a 0 navegaba al
  // dashboard a los 3 s aunque el vendedor estuviera en pleno cobro.
  useEffect(() => {
    avisosRef.current = new Set();
    setExpirada(false);
    if (!ventaData.expiraEn) { setSegundos(null); return undefined; }
    const calc = () => Math.max(0, Math.ceil((new Date(ventaData.expiraEn) - new Date()) / 1000));
    setSegundos(calc());
    const id = setInterval(() => setSegundos(calc()), 1000);
    return () => clearInterval(id);
  }, [ventaData.expiraEn]);

  useEffect(() => {
    if (segundos === null || completed) return;
    const avisar = (clave, fn) => {
      if (avisosRef.current.has(clave)) return;
      avisosRef.current.add(clave);
      fn();
    };
    if (segundos === 0) {
      // Con un cobro Webpay en curso no se interrumpe: el backend decide si la
      // venta sigue siendo cobrable y el modal lo informa al verificar.
      if (cobroEnCurso) {
        avisar('0', () => notify.warning('La reserva de cupos venció. Termina de verificar el pago Webpay.'));
      } else {
        avisar('0', () => setExpirada(true));
      }
    } else if (segundos <= 120) {
      avisar('2', () => notify.error('Quedan 2 minutos para que expire la reserva de cupos.'));
    } else if (segundos <= 300) {
      avisar('5', () => notify.warning('Quedan 5 minutos para que expire la reserva de cupos.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segundos, completed, cobroEnCurso]);

  // Cerrar la pestaña con una venta abierta deja los cupos retenidos hasta que expire.
  useEffect(() => {
    if (!hayVenta) return undefined;
    const aviso = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [hayVenta]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const updateVentaData = (data) => setVentaData(prev => ({ ...prev, ...data }));
  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo({ top: 0 });
    }
  };
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo({ top: 0 });
    }
  };

  // Cancela TODAS las ventas del carrito (antes solo la primera: en multi-taller
  // las demás quedaban reteniendo cupos). Devuelve los errores para mostrarlos.
  const cancelarTodas = async (motivo) => {
    const errores = [];
    for (const id of idsDeVenta(ventaData)) {
      try {
        await VentasPresenciales.CancelarVenta(id, { motivo });
      } catch (e) {
        errores.push(e?.message || 'Error desconocido');
      }
    }
    return errores;
  };

  const resetVenta = () => {
    setVentaData(ESTADO_INICIAL_VENTA);
    setCurrentStep(0);
    setCompleted(false);
    setExpirada(false);
    setEstadoPago('libre');
    ventaCompletadaRef.current = null;
  };

  const confirmarCancelacion = ({ salir }) => {
    if (!hayVenta) { if (salir) navigate('/admin/dashboard'); else resetVenta(); return; }
    const n = idsDeVenta(ventaData).length;
    modal.confirm({
      title: salir ? '¿Salir y cancelar la venta?' : '¿Cancelar la venta?',
      icon: <ExclamationCircleFilled style={{ color: '#dc2626' }} />,
      content: `Se liberarán los cupos reservados${n > 1 ? ` de los ${n} talleres` : ''}. No se ha cobrado nada.`,
      okText: 'Sí, cancelar venta',
      okButtonProps: { danger: true },
      cancelText: 'Seguir con la venta',
      onOk: async () => {
        setLoading(true);
        try {
          const errores = await cancelarTodas('Cancelada por el vendedor');
          if (errores.length > 0) {
            notify.error(`No se pudo cancelar ${errores.length === n ? 'la venta' : `${errores.length} de ${n} ventas`}: ${errores[0]}`);
            return;
          }
          notify.info('Venta cancelada y cupos liberados');
          if (salir) navigate('/admin/dashboard'); else resetVenta();
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const reiniciarTrasExpirar = async () => {
    // El cron también las expira; cancelarlas ya libera los cupos al tiro.
    setLoading(true);
    await cancelarTodas('Reserva expirada').catch(() => {});
    setLoading(false);
    resetVenta();
  };

  const timerStatus = (() => {
    if (segundos === null) return null;
    if (segundos <= 180) return 'critical';
    if (segundos <= 420) return 'warning';
    return 'ok';
  })();

  const renderContent = () => {
    const props = { ventaData, updateVentaData, nextStep, prevStep };
    switch (currentStep) {
      case 0: return <SeleccionTallerHorario {...props} />;
      case 1: return <BusquedaUsuario {...props} />;
      case 2: return <SeleccionPlan {...props} />;
      case 3: return (
        <ResumenYPago
          {...props}
          onEstadoPago={setEstadoPago}
          onReiniciar={resetVenta}
          onComplete={(resumen) => {
            notify.success('¡Venta completada exitosamente!');
            // Congelar snapshot de la venta justo antes de salir del wizard.
            // `totalCobradoFinal` = monto realmente cobrado (el mismo que fue al
            // POS/Webpay y a la boleta), provisto por ResumenYPago. Evita recalcularlo
            // desde items (que no llevan planDetalle por-taller en multi-venta).
            ventaCompletadaRef.current = { ...ventaData, totalCobradoFinal: resumen?.totalCobrado };
            setCompleted(true);
          }}
        />
      );
      default: return null;
    }
  };

  // ── Reserva expirada ───────────────────────────────────────────────────────
  if (expirada && !completed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-start justify-center py-10 px-4">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <Result
            icon={<FieldTimeOutlined style={{ color: '#d97706' }} />}
            title="La reserva de cupos expiró"
            subTitle="No se cobró nada. Los cupos quedan libres para otros; inicia la venta de nuevo."
            extra={[
              <Button key="nueva" type="primary" icon={<PlusOutlined />} loading={loading} onClick={reiniciarTrasExpirar}>
                Iniciar otra venta
              </Button>,
              <Button key="inicio" icon={<HomeOutlined />} onClick={() => navigate('/admin/dashboard')}>
                Volver al inicio
              </Button>,
            ]}
          />
        </div>
      </div>
    );
  }

  // ── Pantalla de confirmación post-venta ────────────────────────────────────
  if (completed) {
    const v = ventaCompletadaRef.current || {};
    const items = v.items?.length > 0 ? v.items : (v.codigoVenta ? [v] : []);
    const cliente = v.inscrito || v.usuario || {};
    const planNombre = v.planDetalle?.nombre || v.planNombre || '–';
    // Total cobrado: usar el monto real capturado al completar la venta (el que
    // fue al POS/Webpay y a la boleta). Fallback defensivo: sumar montoFinal por
    // item — NO valorFinal, que es el precio por horario, no el total del taller.
    const totalCobrado = Number(
      v.totalCobradoFinal ??
      items.reduce(
        (acc, it) => acc + Number(
          it.planDetalle?.montoFinal ?? it.planDetalle?.valorFinal ??
          v.planDetalle?.montoFinal ?? v.planDetalle?.valorFinal ?? 0
        ),
        0
      )
    );

    return (
      <div className="min-h-screen bg-slate-50 flex items-start justify-center py-10 px-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="h-1.5 bg-[#feda2c]" />
          <Result
            status="success"
            icon={<CheckCircleFilled style={{ color: '#0047bb' }} />}
            title={<>¡Venta <span className="hl-amarillo">completada</span>!</>}
            subTitle={
              items.length > 1
                ? `${items.length} inscripciones registradas correctamente`
                : 'La inscripción fue registrada correctamente'
            }
          />

          <div className="px-8 pb-6 -mt-4">
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={{ width: 180, background: '#f8fafc', fontWeight: 600 }}
            >
              {items.length > 0 && (
                <Descriptions.Item label={items.length > 1 ? 'Códigos de venta' : 'Código de venta'}>
                  <Space wrap size={[6, 6]}>
                    {items.map((it, i) => (
                      <Tag key={i} color="blue" style={{ fontFamily: 'monospace', fontSize: 13 }}>
                        {it.codigoVenta || '—'}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              {(cliente.nombre || cliente.rut) && (
                <Descriptions.Item label="Cliente">
                  {cliente.nombre || '—'}
                  {cliente.rut && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({cliente.rut})
                    </Text>
                  )}
                </Descriptions.Item>
              )}
              {items.length > 0 && (
                <Descriptions.Item label={items.length > 1 ? 'Talleres' : 'Taller'}>
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    {items.map((it, i) => (
                      <div key={i}>
                        {it.taller?.nombre || v.taller?.nombre || '—'}
                      </div>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Plan">
                <span className="capitalize">{planNombre}</span>
              </Descriptions.Item>
              {totalCobrado > 0 && (
                <Descriptions.Item label="Total cobrado">
                  <Text strong style={{ fontSize: 16, color: '#00266b' }}>
                    {formatCLP(totalCobrado)}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          <div className="px-8 pb-8 flex flex-col sm:flex-row gap-3">
            <Button
              size="large"
              icon={<PlusOutlined />}
              onClick={resetVenta}
              block
              style={{ background: '#feda2c', borderColor: '#feda2c', color: '#00266b', fontWeight: 600 }}
            >
              Iniciar otra venta
            </Button>
            <Button
              size="large"
              icon={<HomeOutlined />}
              onClick={() => navigate('/admin/dashboard')}
              block
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const motivoBloqueo = cobroEnCurso
    ? 'Hay un cobro Webpay en curso: verifícalo o descarta el QR primero'
    : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 flex-shrink-0">
        <div className="h-1 bg-[#feda2c]" />
        <div className="max-w-6xl mx-auto">

          {/* Top row */}
          <div className="flex items-center justify-between px-4 sm:px-6 h-14 gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <Tooltip title={motivoBloqueo || (hayVenta ? 'Salir (cancela la venta)' : 'Volver al inicio')}>
                <Button
                  type="text" size="small" icon={<ArrowLeftOutlined />}
                  aria-label="Salir"
                  disabled={cobroEnCurso}
                  onClick={() => confirmarCancelacion({ salir: true })}
                  className="text-slate-600"
                />
              </Tooltip>
              <div className="h-5 w-px bg-slate-200" />
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-bold text-[#00266b] text-sm truncate">
                  Nueva venta presencial
                </span>
                {ventaData.items?.length > 1 && (
                  <span className="inline-flex items-center gap-1 bg-[#fffbea] border border-[#fde68a] text-[#5c4300]
                    text-xs font-semibold px-2 py-0.5 rounded-full">
                    {ventaData.items.length} talleres
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {timerStatus && (
                <Tooltip title="Tiempo que quedan reservados los cupos">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                    text-sm font-bold font-mono transition-all ${
                    timerStatus === 'critical'
                      ? 'bg-red-100 text-red-700 animate-pulse'
                      : timerStatus === 'warning'
                      ? 'bg-[#fffbea] text-[#5c4300] border border-[#fde68a]'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    <ClockCircleOutlined />
                    <span>{formatReloj(segundos)}</span>
                  </div>
                </Tooltip>
              )}
              {hayVenta && (
                <Tooltip title={motivoBloqueo}>
                  <Button
                    danger type="default" size="small"
                    icon={<CloseOutlined />}
                    onClick={() => confirmarCancelacion({ salir: true })}
                    loading={loading}
                    disabled={cobroEnCurso}
                  >
                    <span className="hidden sm:inline">Cancelar venta</span>
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Stepper row */}
          <div className="flex items-center px-4 sm:px-6 pb-3 gap-0">
            {STEPS.map((step, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center
                    text-xs font-bold flex-shrink-0 transition-all ${
                    i < currentStep
                      ? 'bg-[#0047bb] text-white'
                      : i === currentStep
                      ? 'bg-[#feda2c] text-[#00266b] ring-4 ring-[#feda2c]/30'
                      : 'bg-slate-200 text-slate-500'
                  }`}>
                    {i < currentStep ? <CheckOutlined style={{ fontSize: 11 }} /> : i + 1}
                  </div>
                  <div className="hidden lg:block">
                    <div className={`text-xs font-semibold leading-none ${
                      i <= currentStep ? 'text-[#00266b]' : 'text-slate-400'
                    }`}>{step.title}</div>
                    <div className="text-xs text-slate-400 leading-tight mt-0.5">
                      {step.subtitle}
                    </div>
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 sm:mx-4 rounded transition-colors ${
                    i < currentStep ? 'bg-[#0047bb]' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </header>

      {/* ── Alerts ────────────────────────────────────────────────────────── */}
      {(timerStatus === 'critical' || ventaData.advertencias?.length > 0) && (
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 pt-4 flex flex-col gap-2">
          {timerStatus === 'critical' && !cobroEnCurso && (
            <Alert
              message="Quedan pocos minutos: completa la venta antes de que expire la reserva de cupos."
              type="error" showIcon
            />
          )}
          {ventaData.advertencias?.length > 0 && (
            <Alert
              message="Advertencias del servidor"
              description={
                <ul className="list-disc pl-4 mt-1">
                  {ventaData.advertencias.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              }
              type="warning" showIcon closable
            />
          )}
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-6">
        {renderContent()}
      </main>

      {/* ── Footer status bar ─────────────────────────────────────────────── */}
      {ventaData.ventaId && (
        <footer className="bg-white border-t border-slate-100 px-4 sm:px-6 py-2 flex-shrink-0">
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#feda2c] animate-pulse flex-shrink-0" />
            <span className="text-xs text-slate-400 font-mono truncate">
              {ventaData.items?.length > 1
                ? `${ventaData.items.length} talleres · ${ventaData.items.map(i => i.codigoVenta).join(' · ')}`
                : `Venta en proceso · ${ventaData.codigoVenta}`}
            </span>
          </div>
        </footer>
      )}
    </div>
  );
};

export default VentasNueva;
